import { createRelayResource } from '@townsquare/relay-utils';
import type { HomeResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';

import StaffDirectoryViewQuery, {
	StaffDirectoryViewQuery as StaffDirectoryViewQueryType,
} from '../components/__generated__/StaffDirectoryViewQuery.graphql';
import { getScreenFromStringOrDefault } from '../config/ScreenConfig';

import { appendEmptyTeamSearchToTql, updateTeamQueryBasedOnScreen } from './utils';

export const staffDirectoryResource = createRelayResource<StaffDirectoryViewQueryType>({
	getQuery: (routerContext, resourceContext) => {
		const context = resourceContext as HomeResourceContext;
		const user = context.user;

		const screen = getScreenFromStringOrDefault(routerContext.query.screen);

		// This will roll out separately from the experiment in case there are issues with the query.
		const isVerifiedTeamsInForYouEnabled = fg('include_recommended_teams_in_for_you_query');

		const isSearchAllScreen = screen == 'SEARCH_ALL';
		const isBrowseAllScreen = screen == 'BROWSE_ALL';
		const isSearchKudosScreen = screen == 'BROWSE_KUDOS';

		// We define a cachekey so that it only updates when the screen changes, not tql etc (those updates are handled by the components)
		const cacheKey = `relay-staff-directory-${screen.toString()}`;

		const teamQuery = updateTeamQueryBasedOnScreen(routerContext.query.tql ?? '', screen, user);
		const recommendedTeamsQuery = `(member = ${user.accountId}) AND (isVerifiedTeamFilter = 'true')`;
		const userQuery = routerContext.query.tql ?? '';
		const kudosQuery = routerContext.query.tql ?? '';
		const organisationId = context.workspace?.organisationId ?? context.orgId ?? '';
		const cloudId = (context.collabContext?.rovoWorkspace || context.workspace?.cloudId) ?? '';

		// we get fewer results for the search all screen as it displays results from people and from teams. (We also fetch more than we intend to display as CPUS has unreliable counting)
		const numResultsToFetch = isSearchAllScreen ? 12 : 20;

		const workspaceId = context.workspace?.globalId ?? '';
		const kudosEnabled = Boolean(workspaceId);

		return {
			query: StaffDirectoryViewQuery,
			variables: {
				teamQuery: appendEmptyTeamSearchToTql(teamQuery),
				recommendedTeamsQuery,
				userQuery,
				kudosQuery,
				workspaceId,
				organisationId,
				cloudId,
				first: numResultsToFetch,
				isSearchAllScreen,
				isBrowseAllScreen,
				isSearchKudosScreen,
				kudosEnabled,
				isVerifiedTeamsInForYouEnabled,
			},
			cacheKey: cacheKey,
			options: { fetchPolicy: 'store-and-network' },
		};
	},
});
